import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD } from "@/components/common";
import { DOT, PILL, useComp } from "./compUtils";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WD = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const SRC = { obligation: "", rh: "RH", maintenance: "Maintenance" };

function range(view, ref) {
  if (view === "week") { const s = new Date(ref); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); const e = new Date(s); e.setDate(e.getDate() + 6); return [s, e]; }
  if (view === "list") { const s = new Date(ref.getFullYear(), ref.getMonth(), 1); return [s, new Date(ref.getFullYear(), ref.getMonth() + 3, 0)]; }
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const s = new Date(first); s.setDate(1 - ((first.getDay() + 6) % 7));
  const e = new Date(s); e.setDate(e.getDate() + 41);
  return [s, e];
}

function Ev({ e, onOpen, full }) {
  const m = useComp();
  const c = e.color || "grey";
  return (
    <button type="button" onClick={() => e.obligation_id && onOpen(e.obligation_id)} data-testid={`cal-ev-${e.obligation_id || e.source}-${e.date}`}
      className={`block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium ${PILL[c]} ${e.projected ? "opacity-60" : ""} ${e.obligation_id ? "hover:ring-1 hover:ring-gray-300" : "cursor-default"}`}
      title={`${e.title}${e.equipment_name ? ` · ${e.equipment_name}` : ""}${e.employee_name ? ` · ${e.employee_name}` : ""}`}>
      {full ? (
        <span className="flex flex-wrap items-center gap-x-2 text-xs">
          <span className={`h-2 w-2 rounded-full ${DOT[c]}`} /><b>{e.title}</b>
          <span className="text-gray-500">{m.families[e.family]}{e.equipment_name ? ` · ${e.equipment_name}` : ""}{e.employee_name ? ` · ${e.employee_name}` : ""}</span>
          {e.responsible_name && <span>Resp. {e.responsible_name}</span>}<span>{m.statuses[e.status] || e.status}</span>
          {e.priority && <span>Priorité {m.priorities[e.priority]}</span>}{e.last_done && <span>Dernière : {fmtDate(e.last_done)}</span>}
          {e.original_due && <span>Initialement {fmtDate(e.original_due)}</span>}{SRC[e.source] && <span className="rounded bg-white/70 px-1">{SRC[e.source]}</span>}{e.projected && <span>(prévisionnel)</span>}
        </span>
      ) : e.title}
    </button>
  );
}

export function CompCalendar({ onOpen, tick }) {
  const [view, setView] = useState("month");
  const [ref, setRef] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [s, e] = useMemo(() => range(view, ref), [view, ref]);
  useEffect(() => { api.get("/compliance/calendar", { params: { start: iso(s), end: iso(e) } }).then((r) => setEvents(r.data)).catch((x) => toast.error(apiErr(x))); }, [s, e, tick]);
  const byDay = useMemo(() => events.reduce((a, x) => { (a[x.date] = a[x.date] || []).push(x); return a; }, {}), [events]);
  const shift = (n) => { const d = new Date(ref); if (view === "week") d.setDate(d.getDate() + 7 * n); else d.setMonth(d.getMonth() + n); setRef(d); };
  const days = []; for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) days.push(new Date(d));
  const label = view === "week" ? `Semaine du ${fmtDate(iso(s))}` : ref.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const todayIso = iso(new Date());
  return (
    <div className={`${CARD} p-3 sm:p-5`} data-testid="comp-calendar">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Précédent" data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-full p-2 hover:bg-gray-100"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[10rem] text-center font-display font-bold capitalize" data-testid="cal-label">{label}</span>
          <button type="button" aria-label="Suivant" data-testid="cal-next" onClick={() => shift(1)} className="rounded-full p-2 hover:bg-gray-100"><ChevronRight className="h-4 w-4" /></button>
          <button type="button" data-testid="cal-today" onClick={() => setRef(new Date())} className="ml-1 rounded-full border px-3 py-1 text-xs font-semibold">Aujourd'hui</button>
        </div>
        <div className="flex gap-1 rounded-full bg-gray-100 p-1">{[["month", "Mois"], ["week", "Semaine"], ["list", "Liste"]].map(([k, l]) => (
          <button type="button" key={k} data-testid={`cal-view-${k}`} onClick={() => setView(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${view === k ? "bg-white shadow" : "text-gray-500"}`}>{l}</button>))}</div>
      </div>
      {view === "list" ? (
        <div className="space-y-3" data-testid="cal-list">
          {Object.keys(byDay).length === 0 && <p className="py-6 text-center text-sm text-gray-400">Aucune échéance sur les 3 prochains mois.</p>}
          {Object.entries(byDay).map(([d, evs]) => <div key={d}><p className="mb-1 text-xs font-bold text-gray-500">{fmtDate(d)}</p><div className="space-y-1">{evs.map((x, i) => <Ev key={i} e={x} onOpen={onOpen} full />)}</div></div>)}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className={`grid min-w-[640px] grid-cols-7 gap-px rounded-2xl bg-gray-100 ${view === "week" ? "" : ""}`}>
            {WD.map((w) => <div key={w} className="bg-white px-2 py-1 text-[11px] font-bold uppercase text-gray-400">{w}</div>)}
            {days.map((d) => { const k = iso(d); const out = view === "month" && d.getMonth() !== ref.getMonth(); return (
              <div key={k} className={`bg-white p-1 ${view === "week" ? "min-h-[16rem]" : "min-h-[6rem]"} ${out ? "opacity-40" : ""}`} data-testid={`cal-day-${k}`}>
                <p className={`mb-1 text-right text-xs ${k === todayIso ? "font-black text-[#065F46]" : "text-gray-400"}`}>{d.getDate()}</p>
                <div className="space-y-0.5">{(byDay[k] || []).slice(0, view === "week" ? 20 : 3).map((x, i) => <Ev key={i} e={x} onOpen={onOpen} />)}
                  {view === "month" && (byDay[k] || []).length > 3 && <p className="text-[10px] text-gray-400">+{byDay[k].length - 3}</p>}</div>
              </div>); })}
          </div>
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-400">Les occurrences pâles sont prévisionnelles (récurrence). Les échéances RH et de maintenance sont affichées pour information, sans donnée médicale.</p>
    </div>
  );
}
